import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import { Button } from "../../components/button";
import { NumberInput } from "../../components/number-input";
import { useLanguage } from "../../utils/useLanguage";
import {
  getSideVP,
  getBattlePoints,
  UNIT_STATUS,
} from "../../utils/battle-scoring";
import {
  setTurn,
  setUnitState,
  setSideValue,
  finishBattle,
  abandonBattle,
} from "../../state/battle";
import { getScenario, getScoringPack, getSecondary } from "./battle-data";
import { getTrackableUnits } from "./battle-units";
import { UnitTracker } from "./UnitTracker";
import { ReferenceDrawer } from "./ReferenceDrawer";

const TABS = [
  { id: "player", label: "Your army" },
  { id: "opponent", label: "Battle" },
  { id: "scoring", label: "Scoring" },
];

const MAX_TURN = 6;

// `sides.player` tracks damage ON your units → feeds the OPPONENT's VP,
// and vice versa. Scenario points / banners follow the same rule: they are
// stored on the side that CONCEDED them.
export const BattleDashboard = () => {
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const battle = useSelector((state) => state.battle.active);
  const list = useSelector((state) =>
    state.lists.find(({ id }) => id === battle?.listId),
  );
  const [tab, setTab] = useState("player");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);

  if (!battle) return null;

  const playerUnits = getTrackableUnits({ list, language });
  const opponentUnits = battle.opponentUnits || [];
  const playerPointsById = Object.fromEntries(
    playerUnits.map((unit) => [unit.id, unit.points]),
  );
  const opponentPointsById = Object.fromEntries(
    opponentUnits.map((unit) => [unit.id, unit.points]),
  );

  const compositionRule = battle.compositionRule;
  const playerVP = getSideVP({
    ...battle.sides.opponent,
    unitStates: battle.sides.opponent.units,
    unitPointsById: opponentPointsById,
    compositionRule,
  });
  const opponentVP = getSideVP({
    ...battle.sides.player,
    unitStates: battle.sides.player.units,
    unitPointsById: playerPointsById,
    compositionRule,
  });

  const pack = getScoringPack(battle.scoringPack);
  const battlePoints = getBattlePoints({ playerVP, opponentVP, pack });
  const scenario = getScenario(battle.scenario);

  const handleStatusChange = ({ side, unit, status }) => {
    dispatch(
      setUnitState({ side, unitId: unit.id, status, points: unit.points }),
    );

    // Auto-flag general/BSB death from own list metadata.
    if (unit.isGeneral || unit.isBSB) {
      const counts =
        status === UNIT_STATUS.DESTROYED || status === UNIT_STATUS.FLED;
      unit.isGeneral &&
        dispatch(setSideValue({ side, key: "generalDead", value: counts }));
      unit.isBSB &&
        dispatch(setSideValue({ side, key: "bsbDead", value: counts }));
    }
  };

  const sideValue = (side, key) => battle.sides[side][key];
  const setValue = (side, key) => (event) =>
    dispatch(setSideValue({ side, key, value: Number(event.target.value) }));
  const toggleValue = (side, key) =>
    dispatch(
      setSideValue({ side, key, value: !battle.sides[side][key] }),
    );

  const finish = () => {
    dispatch(
      finishBattle({
        playerVP,
        opponentVP,
        playerBP: battlePoints.player,
        opponentBP: battlePoints.opponent,
      }),
    );
  };

  return (
    <div className="battle-dashboard">
      {/* Score bar */}
      <div className="battle-score">
        <div
          className={classNames(
            "battle-score__side",
            playerVP >= opponentVP && "battle-score__side--leading",
          )}
        >
          <small>You</small>
          <b>{playerVP}</b>
          {pack?.brackets.length > 0 && <span>{battlePoints.player} BP</span>}
        </div>
        <div className="battle-score__turn">
          <button
            type="button"
            className="battle-score__turn-button"
            onClick={() => dispatch(setTurn(battle.currentTurn - 1))}
            disabled={battle.currentTurn <= 1}
            aria-label="Previous turn"
          >
            ‹
          </button>
          <div>
            <small>Turn</small>
            <b>
              {battle.currentTurn}
              <span className="battle-score__turn-max">/{MAX_TURN}</span>
            </b>
          </div>
          <button
            type="button"
            className="battle-score__turn-button"
            onClick={() => dispatch(setTurn(battle.currentTurn + 1))}
            disabled={battle.currentTurn >= MAX_TURN}
            aria-label="Next turn"
          >
            ›
          </button>
        </div>
        <div
          className={classNames(
            "battle-score__side",
            opponentVP >= playerVP && "battle-score__side--leading",
          )}
        >
          <small>{battle.opponentName || "Opponent"}</small>
          <b>{opponentVP}</b>
          {pack?.brackets.length > 0 && (
            <span>{battlePoints.opponent} BP</span>
          )}
        </div>
      </div>

      {scenario && (
        <p className="battle-dashboard__scenario">
          {scenario.name}
          {battle.firstTurn === "player" ? " · you went first" : " · opponent went first"}
        </p>
      )}

      {/* Tabs */}
      <div className="battle-tabs">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={classNames(
              "battle-tabs__tab",
              tab === id && "battle-tabs__tab--selected",
            )}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className="battle-tabs__tab battle-tabs__tab--apps"
          onClick={() => setDrawerOpen(!drawerOpen)}
        >
          📖
        </button>
      </div>

      <ReferenceDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Your army: your casualties (feeds opponent VP) */}
      {tab === "player" && (
        <div className="battle-dashboard__units">
          {playerUnits.length === 0 && (
            <p className="battle-dashboard__empty">
              No list selected for this battle — use the Scoring tab for
              totals.
            </p>
          )}
          {playerUnits.map((unit) => (
            <UnitTracker
              key={unit.id}
              unit={unit}
              unitState={battle.sides.player.units[unit.id]}
              onStatusChange={(status) =>
                handleStatusChange({ side: "player", unit, status })
              }
            />
          ))}
        </div>
      )}

      {/* Battle: opponent casualties (feeds your VP) */}
      {tab === "opponent" && (
        <div className="battle-dashboard__units">
          {opponentUnits.length === 0 && (
            <p className="battle-dashboard__empty">
              No opponent units entered — use the Scoring tab for totals.
            </p>
          )}
          {opponentUnits.map((unit) => (
            <UnitTracker
              key={unit.id}
              unit={unit}
              unitState={battle.sides.opponent.units[unit.id]}
              onStatusChange={(status) =>
                handleStatusChange({ side: "opponent", unit, status })
              }
            />
          ))}
        </div>
      )}

      {/* Scoring */}
      {tab === "scoring" && (
        <div className="battle-scoring">
          <section>
            <h3>You scored</h3>
            <div className="battle-scoring__row">
              <label htmlFor="player-banners">Banners captured</label>
              <NumberInput
                id="player-banners"
                min={0}
                value={sideValue("opponent", "banners")}
                onChange={setValue("opponent", "banners")}
              />
            </div>
            <div className="battle-scoring__row">
              <label htmlFor="player-scenario">Scenario points</label>
              <NumberInput
                id="player-scenario"
                min={0}
                value={sideValue("opponent", "scenarioPoints")}
                onChange={setValue("opponent", "scenarioPoints")}
              />
            </div>
            <div className="battle-scoring__row">
              <label>Enemy General slain</label>
              <Button
                type={sideValue("opponent", "generalDead") ? "secondary" : "tertiary"}
                onClick={() => toggleValue("opponent", "generalDead")}
              >
                {sideValue("opponent", "generalDead") ? "Yes" : "No"}
              </Button>
            </div>
            <div className="battle-scoring__row">
              <label>Enemy BSB slain</label>
              <Button
                type={sideValue("opponent", "bsbDead") ? "secondary" : "tertiary"}
                onClick={() => toggleValue("opponent", "bsbDead")}
              >
                {sideValue("opponent", "bsbDead") ? "Yes" : "No"}
              </Button>
            </div>
          </section>

          <section>
            <h3>Opponent scored</h3>
            <div className="battle-scoring__row">
              <label htmlFor="opponent-banners">Banners captured</label>
              <NumberInput
                id="opponent-banners"
                min={0}
                value={sideValue("player", "banners")}
                onChange={setValue("player", "banners")}
              />
            </div>
            <div className="battle-scoring__row">
              <label htmlFor="opponent-scenario">Scenario points</label>
              <NumberInput
                id="opponent-scenario"
                min={0}
                value={sideValue("player", "scenarioPoints")}
                onChange={setValue("player", "scenarioPoints")}
              />
            </div>
            <div className="battle-scoring__row">
              <label>Your General slain</label>
              <Button
                type={sideValue("player", "generalDead") ? "secondary" : "tertiary"}
                onClick={() => toggleValue("player", "generalDead")}
              >
                {sideValue("player", "generalDead") ? "Yes" : "No"}
              </Button>
            </div>
            <div className="battle-scoring__row">
              <label>Your BSB slain</label>
              <Button
                type={sideValue("player", "bsbDead") ? "secondary" : "tertiary"}
                onClick={() => toggleValue("player", "bsbDead")}
              >
                {sideValue("player", "bsbDead") ? "Yes" : "No"}
              </Button>
            </div>
          </section>

          {battle.secondaries?.length > 0 && (
            <section>
              <h3>Secondary objectives</h3>
              <ul className="battle-scoring__secondaries">
                {battle.secondaries.map((id) => {
                  const secondary = getSecondary(id);
                  if (!secondary) return null;
                  return (
                    <li key={id}>
                      <b>{secondary.name}</b> (+{secondary.points}) —{" "}
                      {secondary.description} Add to scenario points when
                      scored.
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {pack?.brackets.length > 0 && (
            <section>
              <h3>{pack.name} brackets</h3>
              {pack.verified === false && (
                <p className="battle-dashboard__warning">
                  ⚠ Default brackets — verify against your event pack
                  (scoring-packs.json).
                </p>
              )}
              <table className="battle-drawer__table battle-scoring__brackets">
                <thead>
                  <tr>
                    <th>VP difference</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {pack.brackets.map((bracket, index) => (
                    <tr
                      key={index}
                      className={classNames(
                        bracket === battlePoints.bracket &&
                          "battle-scoring__bracket--current",
                      )}
                    >
                      <td>
                        {bracket.maxDiff !== undefined
                          ? `≤ ${bracket.maxDiff}`
                          : `> ${pack.brackets[index - 1]?.maxDiff ?? 0}`}
                      </td>
                      <td>
                        {bracket.winner}–{bracket.loser}
                        {bracket.label ? ` (${bracket.label})` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}

      {/* Footer actions */}
      <div className="battle-dashboard__actions">
        {confirmFinish ? (
          <>
            <span>
              Finish? {playerVP}–{opponentVP}
              {pack?.brackets.length > 0 &&
                ` (${battlePoints.player}–${battlePoints.opponent} BP)`}
            </span>
            <Button type="primary" onClick={finish}>
              Confirm
            </Button>
            <Button type="secondary" onClick={() => setConfirmFinish(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button type="primary" onClick={() => setConfirmFinish(true)}>
              Finish battle
            </Button>
            <Button
              type="text"
              onClick={() =>
                window.confirm("Abandon this battle? Nothing will be saved.") &&
                dispatch(abandonBattle())
              }
            >
              Abandon
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
